import { cbSemanticCssVars, typographyCss, colors } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { MAX_SIZE_MB, TEST_IDS } from './constants';

type DocumentDropzoneProps = {
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onSelectFile: (file: File) => void;
  hasError?: boolean;
  isLoading?: boolean;
};

const DropzoneArea = styled.div<{ dragActive: boolean; hasError?: boolean }>`
  ${typographyCss.Body3()}
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
  border: 2px dashed ${({ dragActive }) => (dragActive ? colors.wolt100 : cbSemanticCssVars.borderPrimary)};
  cursor: pointer;
  display: flex;
  justify-content: center;
  width: 100%;
  padding-block: 20px;

  &:focus {
    border-color: ${({ hasError }) => (hasError ? colors.strawberry100 : colors.wolt100)};
    outline: none;
  }
`;

const Prompt = styled(T.Body3)`
  font-weight: 500;
  margin-block-end: 8px;
`;

const PromptAction = styled(T.Body3)`
  color: ${colors.wolt100};
`;

const ErrorFile = styled(T.Body3)`
  color: ${colors.strawberry100};
`;

const SupportsText = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  text-align: center;
`;

export const DocumentDropzone = ({ inputProps, onSelectFile, isLoading, hasError }: DocumentDropzoneProps) => {
  const intl = useIntl();
  const [acceptedFile, setAcceptedFile] = useState<File | null>(null);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': [],
      'image/jpeg': [],
      'application/pdf': [],
      'text/csv': [],
    },
    useFsAccessApi: false,
    multiple: false,
    onDropAccepted: async (files: File[]) => {
      const [file] = files;

      if (!file) {
        return;
      }

      onSelectFile(file);
      setAcceptedFile(file);
    },
  });

  if (isLoading) {
    return (
      <DropzoneArea hasError={hasError} {...getRootProps()} dragActive={isDragActive}>
        <Spinner data-test-id={TEST_IDS.documentDropzoneSpinner} size="medium" />
      </DropzoneArea>
    );
  }

  return (
    <DropzoneArea hasError={hasError} {...getRootProps()} dragActive={isDragActive}>
      <input {...getInputProps()} {...inputProps} />

      {hasError ? (
        <ErrorFile>{acceptedFile?.name}</ErrorFile>
      ) : (
        <>
          <Prompt>
            <PromptAction>{intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.select' })}</PromptAction>{' '}
            {intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.drop' })}
          </Prompt>

          <SupportsText>
            {intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.supported' })}: JPG, PDF, SVG, CSV <br />
            {intl.formatMessage({ id: 'self-serve-sign-up.wizard.upload-page.max-file-size' })}: {MAX_SIZE_MB}MB
          </SupportsText>
        </>
      )}
    </DropzoneArea>
  );
};
